/**
 * ChatMCP LLM Provider for use-hapimcp
 * Bridges chatmcp's LLM infrastructure to use-hapimcp's provider interface
 */

import type {
  LlmProvider,
  LlmProviderConfig,
  LlmResponse,
  ChatMessage,
  ToolDefinition,
  ToolCall,
} from 'use-hapimcp';
import type { LLMSettings, MCPServer } from '@/types';
import { log } from '@/lib/utils';

interface ChatMcpProviderConfig {
  name: string;
  llmSettings: LLMSettings;
  contact: MCPServer;
}

/**
 * Custom LLM provider that uses chatmcp's existing LLM infrastructure
 */
export class ChatMcpProvider implements LlmProvider {
  readonly id: string;
  readonly name: string;
  private llmSettings: LLMSettings;
  private contact: MCPServer;

  constructor(config: ChatMcpProviderConfig) {
    this.id = config.name;
    this.name = config.name;
    this.llmSettings = config.llmSettings;
    this.contact = config.contact;
  }

  /**
   * Send chat request using chatmcp's AI route
   */
  async sendChat(params: {
    messages: ChatMessage[];
    tools?: ToolDefinition[];
    toolChoice?: 'auto' | 'required' | 'none';
    config: LlmProviderConfig;
    signal?: AbortSignal;
    metadata?: Record<string, unknown>;
  }): Promise<LlmResponse> {
    const { messages, tools, config, signal } = params;

    try {
      // Convert messages to chatmcp UI format (exact match for existing API)
      const uiMessages = messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        parts: [{ type: 'text', text: msg.content }],
        metadata: {
          ...msg.metadata,
          // Include tool calls and results if present
          ...(msg.toolCalls && { toolCalls: msg.toolCalls }),
          ...(msg.toolResults && { toolResults: msg.toolResults }),
        },
      }));

      // Call chatmcp's AI endpoint
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: uiMessages,
          server: this.contact,
          tags: params.metadata?.tags || [],
          contextSnapshot: {
            serverId: this.contact.id,
            serverName: this.contact.name,
            tools: (tools || []).map((t) => ({
              name: t.name,
              description: t.description,
              inputSchema: t.inputSchema,
              enabled: true,
            })),
            resources: [],
            fetchedAt: new Date().toISOString(),
          },
          llm: this.llmSettings,
        }),
        signal,
      });

      if (!response.ok) {
        throw new Error(`LLM request failed: ${response.status} ${response.statusText}`);
      }

      // Parse streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let content = '';
      let toolCalls: ToolCall[] = [];

      if (!reader) {
        throw new Error('No response body');
      }

      log.debug('[ChatMcpProvider] Starting to read response stream');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        log.debug('[ChatMcpProvider] Received chunk:', chunk.substring(0, 200));
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.trim()) continue;
          
          // Handle different SSE formats
          let jsonStr = line;
          if (line.startsWith('0:')) {
            jsonStr = line.substring(2);
          } else if (line.startsWith('data: ')) {
            jsonStr = line.substring(6);
          } else if (!line.startsWith('{')) {
            // Skip non-JSON lines
            continue;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            log.debug('[ChatMcpProvider] Parsed JSON:', parsed);

            // Handle AI SDK streaming format
            if (parsed.type === 'text-delta' && parsed.delta) {
              // Accumulate text deltas from AI SDK streaming
              content += parsed.delta;
            } else if (parsed.type === 'tool-call') {
              // Handle tool calls
              toolCalls.push({
                id: parsed.toolCallId || `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                name: parsed.toolName,
                arguments: parsed.args || {},
              });
            }

            // Legacy: Extract text content from parts structure (if still used)
            if (parsed.parts) {
              for (const part of parsed.parts) {
                if (part.type === 'text' && part.text) {
                  content += part.text;
                }
                if (part.type === 'tool-call') {
                  toolCalls.push({
                    id: part.toolCallId || `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                    name: part.toolName,
                    arguments: part.args || {},
                  });
                }
              }
            }
            
            // Legacy: Handle direct text content
            if (parsed.text) {
              content += parsed.text;
            }
            if (parsed.content) {
              content += parsed.content;
            }
          } catch (parseError) {
            // Not JSON, might be plain text
            log.debug('[ChatMcpProvider] Non-JSON line:', line);
          }
        }
      }

      log.debug('[ChatMcpProvider] Final content:', content.substring(0, 200));
      log.debug('[ChatMcpProvider] Tool calls:', toolCalls);

      return {
        choices: [
          {
            message: {
              role: 'assistant',
              content,
              toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
            },
            finishReason: 'stop',
          },
        ],
      };
    } catch (error) {
      log.error('[ChatMcpProvider] Generation failed:', error);
      throw error instanceof Error ? error : new Error('LLM generation failed');
    }
  }

  /**
   * Update LLM settings
   */
  updateSettings(llmSettings: LLMSettings): void {
    this.llmSettings = llmSettings;
  }

  /**
   * Update contact
   */
  updateContact(contact: MCPServer): void {
    this.contact = contact;
  }
}
